import { useEffect, useRef, type MutableRefObject } from 'react';
import type { VideoJsPlayer } from 'video.js';
import type { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

const SEEK_EPSILON = 0.05;
const PLAYER_ATTACH_RETRY_MS = 200;

type PlaybackContext = IAssetViewerStateContextValue;

type UseComparePlaybackSyncArgs = {
  enabled: boolean;
  left: PlaybackContext | null;
  right: PlaybackContext | null;
  versionKey: string;
};

type SyncValueArgs<T> = {
  left: T;
  right: T;
  prevLeft: MutableRefObject<T | undefined>;
  prevRight: MutableRefObject<T | undefined>;
  setLeft: (value: T) => void;
  setRight: (value: T) => void;
};

function syncValue<T>({
  left,
  right,
  prevLeft,
  prevRight,
  setLeft,
  setRight
}: SyncValueArgs<T>): void {
  const leftChanged = prevLeft.current !== left;
  const rightChanged = prevRight.current !== right;
  prevLeft.current = left;
  prevRight.current = right;

  if (left === right) return;

  if (leftChanged && !rightChanged) {
    setRight(left);
    prevRight.current = left;
    return;
  }

  if (rightChanged && !leftChanged) {
    setLeft(right);
    prevLeft.current = right;
    return;
  }

  setRight(left);
  prevRight.current = left;
}

function attachPlayerMirror(
  leftPlayer: VideoJsPlayer,
  rightPlayer: VideoJsPlayer,
  leftContext: PlaybackContext,
  rightContext: PlaybackContext,
  isActive: () => boolean
): () => void {
  let isInternalSync = false;

  const runSync = (fn: () => void) => {
    if (isInternalSync || !isActive()) return;
    isInternalSync = true;
    try {
      fn();
    } finally {
      queueMicrotask(() => {
        isInternalSync = false;
      });
    }
  };

  const createSeekSync = (
    sourcePlayer: VideoJsPlayer,
    targetPlayer: VideoJsPlayer,
    sourceContext: PlaybackContext,
    targetContext: PlaybackContext
  ) => {
    return () => {
      if (!isActive() || isInternalSync) return;
      const currentTime = sourcePlayer.currentTime();
      if (Math.abs(currentTime - targetPlayer.currentTime()) < SEEK_EPSILON) {
        return;
      }
      runSync(() => {
        const end =
          sourceContext.playerTime.length > 1
            ? sourceContext.playerTime[1] ?? null
            : null;
        sourceContext.setPlayerTime([currentTime, end]);
        targetContext.setPlayerTime([currentTime, end]);
        targetPlayer.currentTime(Math.max(0, currentTime));
      });
    };
  };

  const createPlaySync = (targetPlayer: VideoJsPlayer) => () => {
    runSync(() => {
      if (!targetPlayer.paused()) return;
      targetPlayer.play()?.catch(() => undefined);
    });
  };

  const createPauseSync = (targetPlayer: VideoJsPlayer) => () => {
    runSync(() => {
      if (targetPlayer.paused()) return;
      targetPlayer.pause();
    });
  };

  const createVolumeSync =
    (sourcePlayer: VideoJsPlayer, targetPlayer: VideoJsPlayer) => () => {
      runSync(() => {
        try {
          targetPlayer.volume(sourcePlayer.volume());
          targetPlayer.muted(sourcePlayer.muted());
        } catch {
          // player disposed
        }
      });
    };

  const createPlaybackRateSync =
    (sourcePlayer: VideoJsPlayer, targetPlayer: VideoJsPlayer) => () => {
      runSync(() => {
        try {
          targetPlayer.playbackRate(sourcePlayer.playbackRate());
        } catch {
          // player disposed
        }
      });
    };

  const listeners = [
    {
      player: leftPlayer,
      event: 'seeked',
      handler: createSeekSync(
        leftPlayer,
        rightPlayer,
        leftContext,
        rightContext
      )
    },
    {
      player: rightPlayer,
      event: 'seeked',
      handler: createSeekSync(
        rightPlayer,
        leftPlayer,
        rightContext,
        leftContext
      )
    },
    { player: leftPlayer, event: 'play', handler: createPlaySync(rightPlayer) },
    {
      player: rightPlayer,
      event: 'play',
      handler: createPlaySync(leftPlayer)
    },
    {
      player: leftPlayer,
      event: 'pause',
      handler: createPauseSync(rightPlayer)
    },
    {
      player: rightPlayer,
      event: 'pause',
      handler: createPauseSync(leftPlayer)
    },
    {
      player: leftPlayer,
      event: 'volumechange',
      handler: createVolumeSync(leftPlayer, rightPlayer)
    },
    {
      player: rightPlayer,
      event: 'volumechange',
      handler: createVolumeSync(rightPlayer, leftPlayer)
    },
    {
      player: leftPlayer,
      event: 'ratechange',
      handler: createPlaybackRateSync(leftPlayer, rightPlayer)
    },
    {
      player: rightPlayer,
      event: 'ratechange',
      handler: createPlaybackRateSync(rightPlayer, leftPlayer)
    }
  ] as const;

  listeners.forEach(({ player, event, handler }) => {
    player.on(event, handler);
  });

  return () => {
    listeners.forEach(({ player, event, handler }) => {
      player.off(event, handler);
    });
  };
}

export function useComparePlaybackSync({
  enabled,
  left,
  right,
  versionKey
}: UseComparePlaybackSyncArgs): void {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const versionKeyRef = useRef(versionKey);
  const uiInitialized = useRef(false);
  const previousLeftTimeFormat = useRef(left?.timeFormat);
  const previousRightTimeFormat = useRef(right?.timeFormat);
  const previousLeftTimecode = useRef(left?.isTimecodeChecked);
  const previousRightTimecode = useRef(right?.isTimecodeChecked);

  useEffect(() => {
    if (versionKeyRef.current !== versionKey) {
      versionKeyRef.current = versionKey;
      uiInitialized.current = false;
    }

    if (!enabled || !left || !right) {
      uiInitialized.current = false;
      return;
    }

    if (!uiInitialized.current) {
      previousLeftTimeFormat.current = left.timeFormat;
      previousRightTimeFormat.current = right.timeFormat;
      previousLeftTimecode.current = left.isTimecodeChecked;
      previousRightTimecode.current = right.isTimecodeChecked;

      if (right.timeFormat !== left.timeFormat) {
        right.setTimeFormat(left.timeFormat);
        previousRightTimeFormat.current = left.timeFormat;
      }
      if (right.isTimecodeChecked !== left.isTimecodeChecked) {
        right.setIsTimecodeChecked(left.isTimecodeChecked);
        previousRightTimecode.current = left.isTimecodeChecked;
      }

      uiInitialized.current = true;
      return;
    }

    syncValue({
      left: left.timeFormat,
      right: right.timeFormat,
      prevLeft: previousLeftTimeFormat,
      prevRight: previousRightTimeFormat,
      setLeft: left.setTimeFormat,
      setRight: right.setTimeFormat
    });
    syncValue({
      left: left.isTimecodeChecked,
      right: right.isTimecodeChecked,
      prevLeft: previousLeftTimecode,
      prevRight: previousRightTimecode,
      setLeft: left.setIsTimecodeChecked,
      setRight: right.setIsTimecodeChecked
    });
  }, [
    enabled,
    left,
    right,
    versionKey,
    left?.timeFormat,
    right?.timeFormat,
    left?.isTimecodeChecked,
    right?.isTimecodeChecked
  ]);

  useEffect(() => {
    if (!enabled || !left || !right) return;

    let canceled = false;
    let detachListeners: (() => void) | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    const isActive = () => enabledRef.current;

    const attachPlayers = () => {
      if (canceled) return;
      const leftPlayer = left.getPlaybackPlayer();
      const rightPlayer = right.getPlaybackPlayer();
      if (!leftPlayer || !rightPlayer) {
        retryTimeout = setTimeout(attachPlayers, PLAYER_ATTACH_RETRY_MS);
        return;
      }
      detachListeners?.();
      detachListeners = attachPlayerMirror(
        leftPlayer,
        rightPlayer,
        left,
        right,
        isActive
      );
    };

    attachPlayers();

    return () => {
      canceled = true;
      if (retryTimeout != null) clearTimeout(retryTimeout);
      detachListeners?.();
    };
  }, [enabled, left, right, versionKey]);
}
